import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's CTA button
 * @property {HTMLElement} buttonText - The button text span
 * @property {HTMLElement} buttonPrice - The button price container
 * @property {HTMLButtonElement} [wishlistButton] - The wishlist heart button
 * @property {HTMLElement} [ctaContainer] - The CTA container
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * Two CTA modes:
 * - Modal: opens the variant-modal drawer (products with variants in modal mode)
 * - Direct: puppets the page's add-to-cart button click
 *
 * Wishlist: puppets the Wishlist King app button (wishlist-button-block) and
 * mirrors its wk-selected state via MutationObserver.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {MutationObserver | null} */
  #wishlistObserver = null;

  /** @type {MutationObserver | null} */
  #wishlistContainerObserver = null;

  /** @type {HTMLButtonElement | null} */
  #observedWishlistBtn = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();
    this.#setupWishlistSync();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    section?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#wishlistObserver?.disconnect();
    this.#wishlistContainerObserver?.disconnect();
    this.#abortController.abort();
  }

  /** @returns {boolean} */
  get #useModal() {
    // The trigger attribute also covers notify-only mode: sold-out
    // single-variant products whose CTA opens the back-in-stock modal.
    return (
      this.dataset.hasVariants === 'true' ||
      this.refs.addToCartButton?.hasAttribute('data-variant-modal-trigger')
    );
  }

  /**
   * Sets up IntersectionObservers to show/hide the sticky bar.
   *
   * Behaviour (matches Figma design):
   * - Bar is VISIBLE on page load by default.
   * - Hides when the main buy-buttons block is visible in the viewport.
   * - Shows again when the main buy-buttons block leaves the viewport (any direction).
   * - Hides when the footer becomes visible (prevent overlap).
   * - Shows again when footer leaves viewport (and buy-buttons are still off-screen).
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // Show sticky bar immediately — it hides once the ATC enters the viewport
    this.#showStickyBar();

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) {
      // No footer found — just observe buy buttons without footer hide logic
      this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
        const [entry] = entries;
        if (!entry) return;
        if (entry.isIntersecting) {
          this.#hideStickyBar();
        } else {
          this.#showStickyBar();
        }
      });
      this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
      return;
    }

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (entry.isIntersecting) {
        // Main ATC is visible — hide sticky bar
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      } else if (!this.#hiddenByBottom) {
        // Main ATC left viewport and footer isn't covering us — show sticky bar
        this.#showStickyBar();
      }
    });

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer left viewport — check if buy-buttons are also off screen
          const rect = buyButtonsBlock.getBoundingClientRect();
          const buyButtonsVisible = rect.top < window.innerHeight && rect.bottom > 0;
          if (!buyButtonsVisible) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      { rootMargin: '200px 0px 0px 0px' }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
  }

  /**
   * Finds the Wishlist King button (button.wk-button inside wishlist-button-block).
   * @returns {HTMLButtonElement | null}
   */
  #getWishlistAppButton() {
    return this.closest('.shopify-section')?.querySelector(
      'wishlist-button-block wk-button button.wk-button'
    ) ?? null;
  }

  /**
   * Reads wk-selected class from Wishlist King button and syncs to our sticky button.
   */
  #syncWishlistState() {
    const { wishlistButton } = this.refs;
    if (!wishlistButton) return;

    const appBtn = this.#getWishlistAppButton();
    if (!appBtn) return;

    const isWishlisted = appBtn.classList.contains('wk-selected');
    wishlistButton.dataset.wishlisted = String(isWishlisted);
    wishlistButton.setAttribute('aria-pressed', String(isWishlisted));
  }

  /**
   * Attaches MutationObservers to keep the sticky wishlist button in sync.
   *
   * Two observers:
   * 1. **Button observer** — watches the wk-button's class attribute for
   *    wk-selected toggles (the normal add/remove flow).
   * 2. **Container observer** — watches the wishlist-button-block's subtree
   *    for childList mutations. Wishlist King sometimes replaces the entire
   *    button node (e.g. after an API round-trip), which silently detaches
   *    the button observer. When that happens we re-scan and re-attach.
   *
   * Safe to call multiple times — disconnects previous observers first.
   */
  #attachWishlistObserver() {
    const appBtn = this.#getWishlistAppButton();
    if (!appBtn || appBtn === this.#observedWishlistBtn) return;

    this.#wishlistObserver?.disconnect();
    this.#wishlistContainerObserver?.disconnect();
    this.#observedWishlistBtn = appBtn;

    this.#syncWishlistState();

    // 1. Watch the button's class for wk-selected changes
    this.#wishlistObserver = new MutationObserver(() => this.#syncWishlistState());
    this.#wishlistObserver.observe(appBtn, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // 2. Watch the container for DOM replacement of the button node
    const container = appBtn.closest('wishlist-button-block') ?? appBtn.closest('wk-button');
    if (container) {
      this.#wishlistContainerObserver = new MutationObserver(() => {
        const currentBtn = this.#getWishlistAppButton();
        if (currentBtn && currentBtn !== this.#observedWishlistBtn) {
          // Button node was replaced — re-attach
          this.#observedWishlistBtn = null;
          this.#attachWishlistObserver();
        }
        // Always re-sync in case the state changed during replacement
        this.#syncWishlistState();
      });
      this.#wishlistContainerObserver.observe(container, {
        childList: true,
        subtree: true,
      });
    }
  }

  /**
   * Initial wishlist setup. Wishlist King renders asynchronously as an app block,
   * so we poll until the button appears or give up after ~5 seconds.
   * Each retry is guarded by the abort signal so it won't fire after disconnect.
   */
  #setupWishlistSync() {
    this.#attachWishlistObserver();
    if (this.#observedWishlistBtn) return;

    const { signal } = this.#abortController;
    const delays = [500, 1000, 1500, 2000];
    let i = 0;

    const tryAttach = () => {
      if (signal.aborted || this.#observedWishlistBtn) return;
      this.#attachWishlistObserver();
      if (!this.#observedWishlistBtn && i < delays.length) {
        setTimeout(tryAttach, delays[i++]);
      }
    };

    setTimeout(tryAttach, delays[i++]);
  }

  /**
   * Handles add to cart button click in sticky bar.
   * Modal mode: opens variant-modal. Direct mode: sets skip flag and calls
   * requestSubmit so Monster Upsell intercepts once (prevents double-add).
   */
  handleAddToCartClick = () => {
    if (this.#useModal) {
      /** @type {any} */ (
        this.closest('.shopify-section')?.querySelector('variant-modal')
      )?.open();
      return;
    }

    const productForm = this.#getProductForm();
    if (!productForm) return;

    const form = /** @type {HTMLFormElement | null} */ (productForm.querySelector('form'));
    if (!form) return;

    // Flag for product-form-component to skip its own handleSubmit so Monster
    // Upsell intercepts the submit event once instead of double-adding.
    productForm.dataset.skipNextSubmit = 'true';
    form.requestSubmit();
  };

  /**
   * Handles wishlist button click — puppets Wishlist King app button.
   * If the observer lost track (e.g. app re-rendered), re-scans before clicking.
   * After the click, polls briefly to catch the state change in case the app
   * replaces the DOM node (which would orphan our MutationObserver).
   * Falls back to /pages/wishlist if the app button is still not found.
   */
  handleWishlistClick = () => {
    let appBtn = this.#getWishlistAppButton();

    // Re-attach observer if the app button changed or was re-rendered
    if (appBtn && appBtn !== this.#observedWishlistBtn) {
      this.#observedWishlistBtn = null;
      this.#attachWishlistObserver();
      appBtn = this.#getWishlistAppButton();
    }

    if (!appBtn) {
      // Locale-aware root (always ends in "/", e.g. "/" or "/en/") so the
      // fallback doesn't drop the visitor onto the default-locale page.
      const routesRoot = window.Shopify?.routes?.root || '/';
      window.location.href = `${routesRoot}pages/wishlist`;
      return;
    }

    appBtn.click();

    // Post-click safety net: Wishlist King may replace the button node after its
    // API call, which orphans the MutationObserver. Poll a few times to re-attach
    // and sync state so the user doesn't have to click twice.
    const { signal } = this.#abortController;
    const postClickDelays = [150, 400, 800];
    let i = 0;

    const postClickSync = () => {
      if (signal.aborted) return;
      const currentBtn = this.#getWishlistAppButton();
      if (currentBtn && currentBtn !== this.#observedWishlistBtn) {
        this.#observedWishlistBtn = null;
        this.#attachWishlistObserver();
      }
      this.#syncWishlistState();
      if (i < postClickDelays.length) {
        setTimeout(postClickSync, postClickDelays[i++]);
      }
    };

    setTimeout(postClickSync, postClickDelays[i++]);
  };

  /**
   * Handles variant:update — morphs sticky bar content and re-caches refs.
   * Also re-attaches the wishlist observer because morph replaces DOM nodes.
   * @param {Event} event
   */
  #handleVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (detail.data.productId !== this.dataset.productId) return;

    const variant = detail.resource;
    const newStickyAddToCart = detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') ?? 'false';

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    if (variant?.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-attach wishlist observer — morph replaces the wishlist button DOM node
    // Wishlist King may also re-render its button asynchronously after variant change
    this.#observedWishlistBtn = null;
    this.#attachWishlistObserver();
    if (!this.#observedWishlistBtn) {
      const { signal } = this.#abortController;
      const retries = [300, 800, 1500];
      let i = 0;
      const retry = () => {
        if (signal.aborted || this.#observedWishlistBtn) return;
        this.#attachWishlistObserver();
        if (!this.#observedWishlistBtn && i < retries.length) {
          setTimeout(retry, retries[i++]);
        }
      };
      setTimeout(retry, retries[i++]);
    }
  };

  /**
   * @param {Event} event
   */
  #handleVariantSelected = (event) => {
    const variantId = /** @type {any} */ (event).detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  #showStickyBar() {
    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
